import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Link, styled } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { TSettingsField } from 'src/@types';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import RHFUploadDynamic from 'src/components/hook-form/RHFUploadDynamic';
import { UploadErrorMessage } from 'src/components/hook-form/UploadErrorMessage';
import Iconify from 'src/components/iconify';
import { DeleteButton, FormInnerContainer, FormLabel, FormRow } from 'src/layouts/form/FormLayout';
import { downloadApi } from 'src/service';
import { FORM_IDS, FormType } from 'src/static/constants';
import { getOriginalFileName } from 'src/utils/fileUploadUtils';

import { DocumentEditor } from './components/DocumentEditor';
import { DocumentViewer } from './components/DocumentViewer';
import { useCompanyInfoByLanguage } from './hooks/useCompanyInfoByLangCd';
import BasicInfo from './BasicInfo';
import Location from './Location';

interface ISettingsFormProps {
  langCd: string;
}
export const SettingsForm = ({ langCd }: ISettingsFormProps) => {
  const { t } = useTranslation();
  const { openDialog } = useDialogContext();
  const methods = useFormContext();
  // 언어에 해당하는 회사 정보, index 반환
  const { companyInfos, index } = useCompanyInfoByLanguage(langCd);
  const { watch, setValue, formState } = methods;

  // 현재 파일과 경로 값을 실시간으로 감시
  const currentFile = watch(`companyInfos.${index}.companyIntroFile`);
  const currentPath = watch(`companyInfos.${index}.companyIntroPath`);

  // 약관 작성 에디터 dialog 오픈
  const handleOpenDocumentEditor = (name: string) => {
    openDialog({
      id: FORM_IDS.SETTINGS_EDITOR,
      formType: FormType.CREATE,
      maxWidth: 'xl',
      title: t(`settings.form.${name}`),
      dialogContent: (
        <DocumentEditor name={`companyInfos.${index}.${name}`} settingsFormMethods={methods} />
      ),
    });
  };

  // 회사소개서 업로드한 파일 이름
  const companyIntroPathName = getOriginalFileName(currentPath);

  // 지원서 다운로드
  const handleDownload = async () => {
    try {
      const attachment = {
        fileNm: companyIntroPathName,
        langCd,
      };
      await downloadApi.downloadCompanyIntro(attachment);
    } catch (error) {
      console.error(error);
    }
  };
  return (
    <Box>
      {/* 기본 정보 */}
      <BasicInfo langCd={langCd} />
      {/* 위치 정보 */}
      <Location langCd={langCd} />
      {/* 약관 작성 및 수정 */}
      {['privacyPolicy', 'terms'].map((name) => (
        <FormInnerContainer key={name}>
          <FormLabel>{t(`settings.form.${name}`)}</FormLabel>
          <DocumentViewer
            key={`companyInfos.${index}.${name}`}
            name={`companyInfos.${index}.${name}`}
            onClick={() => handleOpenDocumentEditor(name)}
          />
        </FormInnerContainer>
      ))}

      {/* 회사 소개서 업로드 */}
      <FormInnerContainer>
        <FormLabel>{t('settings.form.companyIntro')}</FormLabel>
        <FormRow sx={{ gridTemplateColumns: '1fr' }}>
          <StyledUploadContainer>
            {/* preview */}
            {currentFile || currentPath ? (
              <StyledPreviewContainer>
                <Link sx={{ cursor: 'pointer' }} onClick={handleDownload}>
                  {currentFile?.name || companyIntroPathName}
                </Link>
                <DeleteButton
                  onClick={() => {
                    if (index !== -1) {
                      setValue(`companyInfos.${index}.companyIntroFile`, null);
                      setValue(`companyInfos.${index}.companyIntroPath`, null);
                    }
                  }}
                >
                  <Iconify icon="eva:close-fill" width={20} />
                </DeleteButton>
              </StyledPreviewContainer>
            ) : (
              // upload
              <RHFUploadDynamic
                name={`companyInfos.${companyInfos.findIndex(
                  (info: TSettingsField) => info.langCd === langCd,
                )}.companyIntroFile`}
                accept={{
                  'application/pdf': [],
                  'application/msword': [],
                }}
                files={[]}
                onChange={(files) => {
                  if (index !== -1 && files[0]) {
                    setValue(`companyInfos.${index}.companyIntroFile`, files[0]);
                    setValue(`companyInfos.${index}.companyIntroPath`, null);
                  }
                }}
                maxFiles={1}
              />
            )}

            {formState.errors?.companyIntroFile?.message && (
              <UploadErrorMessage message={String(formState.errors?.companyIntroFile?.message)} />
            )}
          </StyledUploadContainer>
        </FormRow>
      </FormInnerContainer>
    </Box>
  );
};

export default observer(SettingsForm);

const StyledUploadContainer = styled(Box)({
  display: 'flex',
  alignItems: 'left',
  flexDirection: 'column',
});
const StyledPreviewContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 1,
});
